// @ds/primitives/IconButton
//
// Square icon-only button. Shape primitive with a variant axis (ghost /
// default / primary / danger / destructive) and a tone axis (controls
// icon color at rest for the "ghost" variant).
//
// Variant semantics mirror Button:
//   ghost       — hover-revealed: faint at rest, secondary color on hover,
//                 transparent bg → surfaceHover on hover. Default.
//   default     — white outline + border, icon visible at rest.
//   primary     — solid blue bg, white icon.
//   danger      — red outline + red icon.
//   destructive — solid red bg, white icon.
//
// For non-ghost variants, delegates to <Button icon={icon} label="">
// so AntD handles sizing, states, and theming consistently.
//
// Tone (ghost only): controls icon color at rest.
//   "neutral"  → TEXT.faint (current default)
//   ToneName   → TONE[t].text at rest, TONE[t].base on hover
//
// Canonical replacement for `MutedIconButton` — keeps the ghost/hover-reveal
// behavior and adds a full variant axis.

import React, { useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import {
  ICON_BUTTON_ICON_SIZE, ICON_BUTTON_SIZE, MOTION, RADIUS, SURFACE, TEXT, TONE,
} from "@ds/tokens/palette";
import type { IconButtonSize, ToneName } from "@ds/tokens/palette";
import { Button } from "@ds/primitives/Button";
import type { ButtonVariant } from "@ds/primitives/Button";

// ─── Public type aliases ──────────────────────────────────────────────────

export type IconButtonVariant = "ghost" | ButtonVariant;
export type IconButtonTone = ToneName | "neutral";
export type { IconButtonSize };

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface IconButtonInput {
  /** Any AntD icon or @ds Icon node. Rendered at the icon font-size for the chosen size. */
  icon: ReactNode;
  /** Required accessible label — no visible text is rendered. */
  ariaLabel: string;
  /**
   * Visual variant. Default `"ghost"` (hover-revealed, transparent at rest).
   * Non-ghost variants render a stable visible state via Button.
   */
  variant?: IconButtonVariant;
  /** Square button size. Default `"xs"` (18 px). */
  size?: IconButtonSize;
  /**
   * Icon color at rest. Only meaningful for `variant="ghost"`.
   * - `"neutral"` (default) → TEXT.faint at rest / TEXT.secondary on hover.
   * - Any `ToneName`        → TONE[t].text at rest / TONE[t].base on hover.
   */
  tone?: IconButtonTone;
  /** When `true`, renders a spinner and disables interaction. */
  loading?: boolean;
  /** When `true`, button is non-interactive and visually muted. */
  disabled?: boolean;
  /** Applied as `data-testid` on the root element. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface IconButtonOutput {
  /** Fires on click. Callers may call `e.stopPropagation()` to prevent row navigation. */
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}

export type IconButtonProps = IconButtonInput & IconButtonOutput;

// ─── Helpers ─────────────────────────────────────────────────────────────

function restColor(tone: IconButtonTone): string {
  if (tone === "neutral") return TEXT.faint;
  return TONE[tone].text;
}

function hoverColor(tone: IconButtonTone): string {
  if (tone === "neutral") return TEXT.secondary;
  return TONE[tone].base;
}

// Map IconButtonSize → Button size. "xxs" and "xs" have no Button equivalent
// (both are sub-md); clamp up to "sm" for the non-ghost path so Button stays
// within its own size enum ("sm" | "md" | "lg").
function toButtonSize(size: IconButtonSize): "sm" | "md" | "lg" {
  if (size === "md") return "md";
  if (size === "sm") return "sm";
  return "sm";
}

// ─── Ghost (hover-reveal) implementation ─────────────────────────────────

function GhostIconButton({
  icon,
  ariaLabel,
  size = "xs",
  tone = "neutral",
  loading,
  disabled,
  testId,
  onClick,
}: IconButtonProps) {
  const [hover, setHover] = useState(false);

  const dim = ICON_BUTTON_SIZE[size];
  const iconSize = ICON_BUTTON_ICON_SIZE[size];

  const isDisabled = disabled || loading;
  const color = isDisabled
    ? TEXT.faint
    : hover
      ? hoverColor(tone)
      : restColor(tone);

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      data-testid={testId}
      disabled={isDisabled}
      onMouseEnter={() => { if (!isDisabled) setHover(true); }}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={{
        display:        "inline-flex",
        alignItems:     "center",
        justifyContent: "center",
        width:          dim,
        height:         dim,
        padding:        0,
        borderRadius:   RADIUS.xs,
        border:         "none",
        background:     hover && !isDisabled ? SURFACE.surfaceHover : "transparent",
        color,
        cursor:         isDisabled ? "not-allowed" : "pointer",
        opacity:        isDisabled ? 0.4 : 1,
        flexShrink:     0,
        transition:     `background ${MOTION.fast} ease, color ${MOTION.fast} ease`,
        fontSize:       iconSize,
      }}
    >
      {loading ? <span className="anticon anticon-loading" /> : icon}
    </button>
  );
}

// ─── Component ────────────────────────────────────────────────────────────

export const IconButton: React.FC<IconButtonProps> = ({
  variant = "ghost",
  ...props
}) => {
  if (variant === "ghost") {
    return <GhostIconButton variant="ghost" {...props} />;
  }

  // Non-ghost: delegate to Button with an empty label so it renders square.
  const { icon, ariaLabel, size = "xs", loading, disabled, testId, onClick } = props;

  return (
    <Button
      label=""
      variant={variant}
      size={toButtonSize(size)}
      icon={icon}
      ariaLabel={ariaLabel}
      loading={loading}
      disabled={disabled}
      testId={testId}
      onClick={onClick as (e: React.MouseEvent<HTMLElement>) => void}
    />
  );
};
